import React, { useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import AceEditor from 'react-ace'
import 'ace-builds/src-noconflict/mode-json'
import 'ace-builds/src-noconflict/theme-xcode'
import { makeStyles } from '@material-ui/core'
import Icon from '../../../../../../atoms/Icon'

// Stable, collision-free fallback for AceEditor's global `name`.
let aceNameCounter = 0
const nextAceName = () => `ddp-tc-${++aceNameCounter}`

const useStyles = makeStyles(theme => ({
  collapsed: {
    width: '100%',
    boxSizing: 'border-box',
    padding: theme.spacing(0.75, 1),
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-neutral-500)',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    cursor: 'pointer',
    borderRadius: 'var(--border-radius-sm)',
    backgroundColor: 'white',
    border: '1px solid var(--color-neutral-300)',
    '&:hover': { backgroundColor: 'var(--color-neutral-100)' }
  },
  toolName: {
    fontFamily: 'var(--font-mono, monospace)',
    fontWeight: 500,
    color: 'var(--color-neutral-800)'
  },
  statusBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '2px',
    padding: '1px 5px',
    borderRadius: 'var(--border-radius-sm)',
    fontSize: '10px',
    fontWeight: 600
  },
  success: {
    backgroundColor: 'var(--color-success-050, #f0fdf4)',
    color: 'var(--color-success-700, #15803d)'
  },
  error: {
    backgroundColor: 'var(--color-danger-050, #fef2f2)',
    color: 'var(--color-danger-700, #b91c1c)'
  },
  pending: {
    backgroundColor: 'var(--color-neutral-100)',
    color: 'var(--color-neutral-500)'
  },
  chevron: {
    marginLeft: 'auto',
    fontSize: '12px',
    color: 'var(--color-neutral-400)'
  },
  expanded: {
    width: '100%',
    boxSizing: 'border-box',
    border: '1px solid var(--color-neutral-300)',
    borderRadius: 'var(--border-radius-sm)',
    backgroundColor: 'white',
    overflow: 'hidden'
  },
  expandedHeader: {
    padding: theme.spacing(0.75, 1),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-neutral-500)',
    cursor: 'pointer',
    borderBottom: '1px solid var(--color-neutral-200)',
    backgroundColor: 'var(--color-neutral-050)',
    '&:hover': { backgroundColor: 'var(--color-neutral-100)' }
  },
  panel: {
    display: 'flex',
    flexDirection: 'column'
  },
  panelHeader: {
    padding: theme.spacing(0.5, 1),
    fontSize: '10px',
    fontWeight: 600,
    color: 'var(--color-neutral-500)',
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
    backgroundColor: 'var(--color-neutral-050)',
    borderBottom: '1px solid var(--color-neutral-200)'
  },
  panelHeaderError: {
    backgroundColor: 'var(--color-danger-050, #fef2f2)',
    color: 'var(--color-danger-700, #b91c1c)'
  },
  editor: {
    height: '180px'
  }
}))

function deriveStatus (toolResult) {
  if (toolResult == null) return 'pending'
  try {
    const parsed = typeof toolResult === 'string' ? JSON.parse(toolResult) : toolResult
    if (parsed && typeof parsed === 'object' && 'success' in parsed) {
      return parsed.success ? 'success' : 'error'
    }
  } catch { /* fall through */ }
  return 'success'
}

function formatJson (value) {
  if (value == null) return ''
  if (typeof value === 'string') {
    try { return JSON.stringify(JSON.parse(value), null, 2) } catch { return value }
  }
  try { return JSON.stringify(value, null, 2) } catch { return String(value) }
}

function ChatToolCall ({ toolCall, toolResult }) {
  const classes = useStyles()
  const [expanded, setExpanded] = useState(false)
  const idRef = useRef(toolCall.id || nextAceName())

  const status = deriveStatus(toolResult)
  const statusLabel = status === 'success' ? 'Success' : status === 'error' ? 'Failed' : 'Running…'
  const statusGlyph = status === 'success' ? '✓' : status === 'error' ? '✕' : '…'

  const requestJson = useMemo(() => {
    const args = toolCall?.arguments ?? toolCall?.args ?? {}
    return formatJson(args)
  }, [toolCall])

  const responseJson = useMemo(() => {
    if (toolResult == null) return ''
    try {
      const parsed = typeof toolResult === 'string' ? JSON.parse(toolResult) : toolResult
      if (parsed && typeof parsed === 'object' && 'success' in parsed) {
        return formatJson(parsed.output ?? parsed.error ?? parsed)
      }
      return formatJson(parsed)
    } catch {
      return typeof toolResult === 'string' ? toolResult : formatJson(toolResult)
    }
  }, [toolResult])

  if (!expanded) {
    return (
      <div className={classes.collapsed} onClick={() => setExpanded(true)}>
        <Icon name='chevronRight' style={{ fontSize: 12, opacity: 0.6 }} />
        <span className={classes.toolName}>{toolCall?.name || 'tool'}</span>
        <span className={`${classes.statusBadge} ${classes[status]}`}>
          <span>{statusGlyph}</span>
          <span>{statusLabel}</span>
        </span>
        <Icon name='chevronDown' className={classes.chevron} />
      </div>
    )
  }

  return (
    <div className={classes.expanded}>
      <div className={classes.expandedHeader} onClick={() => setExpanded(false)}>
        <Icon name='chevronDown' style={{ fontSize: 12, opacity: 0.6 }} />
        <span className={classes.toolName}>{toolCall?.name || 'tool'}</span>
        <span className={`${classes.statusBadge} ${classes[status]}`}>
          <span>{statusGlyph}</span>
          <span>{statusLabel}</span>
        </span>
        <Icon name='chevronUp' className={classes.chevron} />
      </div>
      <div className={classes.panel}>
        <div className={classes.panelHeader}>Request</div>
        <AceEditor
          className={classes.editor}
          mode='json'
          theme='xcode'
          name={`req-${idRef.current}`}
          readOnly
          value={requestJson}
          tabSize={2}
          width='100%'
          showPrintMargin={false}
          fontSize={12}
          setOptions={{ useWorker: false }}
        />
      </div>
      {toolResult != null && (
        <div className={classes.panel}>
          <div className={`${classes.panelHeader} ${status === 'error' ? classes.panelHeaderError : ''}`}>
            Response
          </div>
          <AceEditor
            className={classes.editor}
            mode='json'
            theme='xcode'
            name={`res-${idRef.current}`}
            readOnly
            value={responseJson}
            tabSize={2}
            width='100%'
            showPrintMargin={false}
            fontSize={12}
            setOptions={{ useWorker: false }}
          />
        </div>
      )}
    </div>
  )
}

ChatToolCall.propTypes = {
  toolCall: PropTypes.object.isRequired,
  toolResult: PropTypes.any
}

export default ChatToolCall
